import { ImageShape, ImageTemplate, ResponsiveCropDimensions } from '../ImageGallery.types';
import { generateCropDimensions } from './utils/generateCropDimensions';

export const DEFAULT_IMAGE_RATIOS: Record<ImageShape, number> = {
    square: 1.0, // 1:1
    portrait: 0.75, // 3:4
    landscape: 1.5, // 3:2
    banner: 2.0, // 2:1 wide banner-like
};

export const DEFAULT_LARGE_IMAGE_PIXEL_THRESHOLD = 600000;

// Raw widths only — no heights or ratios hardcoded
const cropConfig = {
    landscapeFull: { widths: [508, 728, 985, 1300], ratio: DEFAULT_IMAGE_RATIOS.landscape },
    landscapeTwoThirds: { widths: [339, 485, 656, 867], ratio: DEFAULT_IMAGE_RATIOS.landscape },
    landscapeThird: { widths: [169, 243, 328, 433], ratio: DEFAULT_IMAGE_RATIOS.landscape },
    landscapeHalf: { widths: [254, 364, 492, 650], ratio: DEFAULT_IMAGE_RATIOS.landscape },
    portraitTwoThirds: { widths: [339, 485, 656, 867], ratio: DEFAULT_IMAGE_RATIOS.portrait },
    portraitHalf: { widths: [254, 364, 492, 650], ratio: DEFAULT_IMAGE_RATIOS.portrait },
    portraitThird: { widths: [169, 243, 328, 433], ratio: DEFAULT_IMAGE_RATIOS.portrait },
    squareHalf: { widths: [254, 364, 492, 650], ratio: DEFAULT_IMAGE_RATIOS.square },
    squareThird: { widths: [169, 243, 328, 433], ratio: DEFAULT_IMAGE_RATIOS.square },
    squareTwoThirds: { widths: [339, 485, 656, 867], ratio: DEFAULT_IMAGE_RATIOS.square },
} as const;

// Single object containing all crops, generated at runtime
export const CROP_DIMS: Record<keyof typeof cropConfig, ResponsiveCropDimensions> = Object.fromEntries(
    Object.entries(cropConfig).map(([key, { widths, ratio }]) => [key, generateCropDimensions(widths, ratio)])
) as Record<keyof typeof cropConfig, ResponsiveCropDimensions>;

export const IMAGE_GRID_DEFAULT_TEMPLATES: ImageTemplate[] = [
    {
        id: 'landscape-solo',
        slots: [
            {
                shape: 'landscape',
                requiresLarge: true,
                gridArea: 'a',
                cropDimensions: CROP_DIMS.landscapeFull,
            },
        ],
        gridTemplateColumns: '1fr',
        gridTemplateAreas: ['a'],
        priority: 10,
    },
    {
        id: 'landscape-stack:portrait',
        slots: [
            {
                shape: 'landscape',
                gridArea: 'a',
                cropDimensions: CROP_DIMS.landscapeHalf,
            },
            {
                shape: 'portrait',
                gridArea: 'b',
                cropDimensions: CROP_DIMS.portraitHalf,
                isSpanning: true,
            },
            {
                shape: 'landscape',
                gridArea: 'c',
                cropDimensions: CROP_DIMS.landscapeHalf,
            },
        ],
        gridTemplateColumns: 'repeat(2, 1fr)',
        gridTemplateAreas: ['a b', 'c b'],
        priority: 8,
    },
    {
        id: 'portrait:landscape-stack',
        slots: [
            {
                shape: 'portrait',
                gridArea: 'a',
                cropDimensions: CROP_DIMS.portraitHalf,
                isSpanning: true,
            },
            {
                shape: 'landscape',
                gridArea: 'b',
                cropDimensions: CROP_DIMS.landscapeHalf,
            },
            {
                shape: 'landscape',
                gridArea: 'c',
                cropDimensions: CROP_DIMS.landscapeHalf,
            },
        ],
        gridTemplateColumns: 'repeat(2, 1fr)',
        gridTemplateAreas: ['a b', 'a c'],
        priority: 8,
    },
    {
        id: 'portrait-pair',
        slots: [
            {
                shape: 'portrait',
                requiresLarge: true,
                gridArea: 'a',
                cropDimensions: CROP_DIMS.portraitHalf,
            },
            {
                shape: 'portrait',
                requiresLarge: true,
                gridArea: 'b',
                cropDimensions: CROP_DIMS.portraitHalf,
            },
        ],
        gridTemplateColumns: 'repeat(2, 1fr)',
        gridTemplateAreas: ['a b'],
        priority: 8,
    },
    {
        id: 'landscape:landscape-stack',
        slots: [
            {
                shape: 'landscape',
                requiresLarge: true,
                gridArea: 'a',
                cropDimensions: CROP_DIMS.landscapeTwoThirds,
                isSpanning: true,
            },
            {
                shape: 'landscape',
                gridArea: 'b',
                cropDimensions: CROP_DIMS.landscapeThird,
            },
            {
                shape: 'landscape',
                gridArea: 'c',
                cropDimensions: CROP_DIMS.landscapeThird,
            },
        ],
        gridTemplateColumns: '2fr 1fr',
        gridTemplateAreas: ['a b', 'a c'],
        priority: 8,
    },
    {
        id: 'landscape-stack:landscape',
        slots: [
            {
                shape: 'landscape',
                gridArea: 'a',
                cropDimensions: CROP_DIMS.landscapeThird,
            },
            {
                shape: 'landscape',
                requiresLarge: true,
                gridArea: 'b',
                cropDimensions: CROP_DIMS.landscapeTwoThirds,
                isSpanning: true,
            },
            {
                shape: 'landscape',
                gridArea: 'c',
                cropDimensions: CROP_DIMS.landscapeThird,
            },
        ],
        gridTemplateColumns: '1fr 2fr',
        gridTemplateAreas: ['a b', 'c b'],
        priority: 8,
    },

    {
        id: 'portrait:portrait-stack',
        slots: [
            {
                shape: 'portrait',
                requiresLarge: true,
                gridArea: 'a',
                cropDimensions: CROP_DIMS.portraitTwoThirds,
                isSpanning: true,
            },
            {
                shape: 'portrait',
                gridArea: 'b',
                cropDimensions: CROP_DIMS.portraitThird,
            },
            {
                shape: 'portrait',
                gridArea: 'c',
                cropDimensions: CROP_DIMS.portraitThird,
            },
        ],
        gridTemplateColumns: '2fr 1fr',
        gridTemplateAreas: ['a b', 'a c'],
        priority: 6,
    },
    {
        id: 'portrait-stack:portrait',
        slots: [
            {
                shape: 'portrait',
                gridArea: 'a',
                cropDimensions: CROP_DIMS.portraitThird,
            },
            {
                shape: 'portrait',
                requiresLarge: true,
                gridArea: 'b',
                cropDimensions: CROP_DIMS.portraitTwoThirds,
                isSpanning: true,
            },
            {
                shape: 'portrait',
                gridArea: 'c',
                cropDimensions: CROP_DIMS.portraitThird,
            },
        ],
        gridTemplateColumns: '1fr 2fr',
        gridTemplateAreas: ['a b', 'c b'],
        priority: 6,
    },
    {
        id: 'landscape-pair',
        slots: [
            {
                shape: 'landscape',
                gridArea: 'a',
                cropDimensions: CROP_DIMS.landscapeHalf,
            },
            {
                shape: 'landscape',
                gridArea: 'b',
                cropDimensions: CROP_DIMS.landscapeHalf,
            },
        ],
        gridTemplateColumns: 'repeat(2, 1fr)',
        gridTemplateAreas: ['a b'],
        priority: 6,
    },
    {
        id: 'portrait-landscape',
        slots: [
            {
                shape: 'portrait',
                gridArea: 'a',
                cropDimensions: CROP_DIMS.portraitThird,
            },
            {
                shape: 'landscape',
                gridArea: 'b',
                cropDimensions: CROP_DIMS.landscapeTwoThirds,
            },
        ],
        gridTemplateColumns: '1fr 2fr',
        gridTemplateAreas: ['a b b'],
        priority: 4,
    },
    {
        id: 'landscape-portrait',
        slots: [
            {
                shape: 'landscape',
                gridArea: 'a',
                cropDimensions: CROP_DIMS.landscapeTwoThirds,
            },
            {
                shape: 'portrait',
                gridArea: 'b',
                cropDimensions: CROP_DIMS.portraitThird,
            },
        ],
        gridTemplateColumns: '2fr 1fr',
        gridTemplateAreas: ['a a b'],
        priority: 4,
    },
    {
        id: 'square-pair',
        slots: [
            {
                shape: 'square',
                gridArea: 'a',
                cropDimensions: CROP_DIMS.squareHalf,
            },
            {
                shape: 'square',
                gridArea: 'b',
                cropDimensions: CROP_DIMS.squareHalf,
            },
        ],
        gridTemplateColumns: 'repeat(2, 1fr)',
        gridTemplateAreas: ['a b'],
        priority: 4,
    },
    {
        id: 'portrait-triplet',
        slots: [
            {
                shape: 'portrait',
                gridArea: 'a',
                cropDimensions: CROP_DIMS.portraitThird,
            },
            {
                shape: 'portrait',
                gridArea: 'b',
                cropDimensions: CROP_DIMS.portraitThird,
            },
            {
                shape: 'portrait',
                gridArea: 'c',
                cropDimensions: CROP_DIMS.portraitThird,
            },
        ],
        gridTemplateColumns: 'repeat(3, 1fr)',
        gridTemplateAreas: ['a b c'],
        priority: 2,
    },
    {
        id: 'square-triplet',
        slots: [
            {
                shape: 'square',
                gridArea: 'a',
                cropDimensions: CROP_DIMS.squareThird,
            },
            {
                shape: 'square',
                gridArea: 'b',
                cropDimensions: CROP_DIMS.squareThird,
            },
            {
                shape: 'square',
                gridArea: 'c',
                cropDimensions: CROP_DIMS.squareThird,
            },
        ],
        gridTemplateColumns: 'repeat(3, 1fr)',
        gridTemplateAreas: ['a b c'],
        priority: 2,
    },
];

export const IMAGE_GRID_HERO_TEMPLATES: {
    [key in 'squares' | 'landscapes']: ImageTemplate;
} = {
    squares: {
        id: 'square:square-stack',
        slots: [
            {
                shape: 'square',
                gridArea: 'a',
                cropDimensions: CROP_DIMS.squareTwoThirds,
                isSpanning: true,
            },
            {
                shape: 'square',
                gridArea: 'b',
                cropDimensions: CROP_DIMS.squareThird,
            },
            {
                shape: 'square',
                gridArea: 'c',
                cropDimensions: CROP_DIMS.squareThird,
            },
        ],
        gridTemplateColumns: '2fr 1fr',
        gridTemplateAreas: ['a b', 'a c'],
        priority: 100,
        mustFill: true,
    },
    landscapes: {
        id: 'landscape:landscape-stack',
        slots: [
            {
                shape: 'landscape',
                requiresLarge: true,
                gridArea: 'a',
                cropDimensions: CROP_DIMS.landscapeTwoThirds,
                isSpanning: true,
            },
            {
                shape: 'landscape',
                gridArea: 'b',
                cropDimensions: CROP_DIMS.landscapeThird,
            },
            {
                shape: 'landscape',
                gridArea: 'c',
                cropDimensions: CROP_DIMS.landscapeThird,
            },
        ],
        gridTemplateColumns: '2fr 1fr',
        gridTemplateAreas: ['a b', 'a c'],
        priority: 100,
        mustFill: true,
    },
};

export const IMAGE_MATCHING_CONFIG = {
    scoreExactLarge: 2.5, // Exact shape match with large image required
    scoreExactNormal: 2.0, // Exact shape match without large image requirement
    scoreWildcardLarge: 2.0, // Wildcard match with large image required
    scoreWildcardNormal: 1.5, // Wildcard match without large image requirement
    wildcardPenalty: 1,
    recencyBasePenalty: 3,
    shapeDistributionPriorityBoost: 10,
};
